// src/pages/AuthPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import Notification from '../components/Notification';
import { getRedirectPath, parseAuthError } from '../utils/authUtils';

const AuthPage = () => {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState('initial'); // 'initial', 'loading', 'mfa', 'success', 'error'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Get auth state and functions from context
    const { isAuthenticated, login, verifyMfa } = useAppContext();

    // Check if already authenticated on mount
    useEffect(() => {
        if (isAuthenticated) {
            setAuthState('success');
            // If already authenticated, redirect to the stored path or dashboard
            const redirectPath = getRedirectPath();
            setTimeout(() => navigate(redirectPath), 1500);
        }
    }, [isAuthenticated, navigate]);

    // Handle email/password form submission
    const handleInitialSubmit = async (e) => {
        e.preventDefault();
        setAuthState('loading');
        setErrorMessage('');

        try {
            // Call login function from context
            const response = await login(email, password);

            if (response.mfa_required) {
                // MFA is required
                setAuthState('mfa');
            } else if (response.authenticated) {
                // No MFA needed, authentication complete
                setAuthState('success');
                // Redirect to the stored path or dashboard after a short delay
                const redirectPath = getRedirectPath();
                setTimeout(() => navigate(redirectPath), 1500);
            } else {
                // Unexpected response
                setAuthState('error');
                setErrorMessage('Unexpected response from server. Please try again.');
            }
        } catch (error) {
            console.error('Login error:', error);

            // Check if error indicates MFA is required
            if (error.response?.data?.mfa_required) {
                setAuthState('mfa');
            } else {
                // Other errors
                setAuthState('error');
                setErrorMessage(
                    error.response?.data?.error ||
                    'Failed to authenticate. Please check your credentials and try again.'
                );
            }
        }
    };

    // Handle MFA form submission
    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setAuthState('loading');
        setErrorMessage('');

        try {
            // Call verifyMfa function from context
            const response = await verifyMfa(mfaCode);

            if (response.authenticated || response.success) {
                // Authentication successful
                setAuthState('success');
                // Redirect to the stored path or dashboard after a short delay
                const redirectPath = getRedirectPath();
                setTimeout(() => navigate(redirectPath), 1500);
            } else {
                // Unexpected response
                setAuthState('error');
                setErrorMessage('Unexpected response from server. Please try again.');
            }
        } catch (error) {
            console.error('MFA error:', error);
            setAuthState('error');
            setErrorMessage(parseAuthError(error));
        }
    };

    // Handle cancellation and returning to the initial state
    const handleCancel = () => {
        setAuthState('initial');
        setErrorMessage('');
        setMfaCode('');
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Financial Runway
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Sign in to your Monarch Money account
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    {/* Initial Login Form */}
                    {authState === 'initial' && (
                        <form className="space-y-6" onSubmit={handleInitialSubmit}>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    Email address
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            {errorMessage && (
                                <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                                    {errorMessage}
                                </div>
                            )}

                            <div>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    fullWidth={true}
                                >
                                    Sign in
                                </Button>
                            </div>

                            <div className="text-center text-sm text-gray-600">
                                <p>
                                    Note: You need a Monarch Money account to use this application.
                                </p>
                                <p className="mt-2">
                                    If you log in to Monarch via Google, you'll need to set a password in your Monarch security settings.
                                </p>
                            </div>
                        </form>
                    )}

                    {/* MFA Form */}
                    {authState === 'mfa' && (
                        <div className="space-y-6">
                            <div className="bg-blue-50 p-4 rounded-md">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-blue-800">
                                            Two-factor authentication required
                                        </h3>
                                        <div className="mt-2 text-sm text-blue-700">
                                            <p>
                                                Please check your authentication app for a code or approve the login request.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleMfaSubmit}>
                                <div>
                                    <label htmlFor="mfa_code" className="block text-sm font-medium text-gray-700">
                                        Authentication Code
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            id="mfa_code"
                                            name="mfa_code"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            autoFocus
                                            required
                                            value={mfaCode}
                                            onChange={(e) => setMfaCode(e.target.value)}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="Enter 6-digit code"
                                        />
                                    </div>
                                </div>

                                {errorMessage && (
                                    <div className="mt-4">
                                        <Notification
                                            type="error"
                                            message={errorMessage}
                                            onClose={() => setErrorMessage('')}
                                            duration={null}
                                        />
                                    </div>
                                )}

                                <div className="mt-6 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="text-sm font-medium text-blue-600 hover:text-blue-500"
                                    >
                                        Back to login
                                    </button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                    >
                                        Verify
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Loading State */}
                    {authState === 'loading' && (
                        <div className="py-8 flex flex-col items-center justify-center">
                            <LoadingSpinner size="large" message="Authenticating..." />
                        </div>
                    )}

                    {/* Success State */}
                    {authState === 'success' && (
                        <div className="p-6 flex flex-col items-center justify-center">
                            <div className="rounded-full bg-green-100 p-3">
                                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="mt-4 text-lg font-medium text-gray-900">Authentication successful!</h3>
                            <p className="mt-1 text-sm text-gray-600">Redirecting to dashboard...</p>
                        </div>
                    )}

                    {/* Error State (when not in initial or MFA state) */}
                    {authState === 'error' && (
                        <div className="p-6 flex flex-col items-center justify-center">
                            <div className="rounded-full bg-red-100 p-3">
                                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <h3 className="mt-4 text-lg font-medium text-gray-900">Authentication failed</h3>
                            <p className="mt-1 text-sm text-gray-600">{errorMessage || 'There was an error authenticating. Please try again.'}</p>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleCancel}
                                className="mt-4"
                            >
                                Try Again
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthPage;